<template>
  <div>
    <h3>What are you working on today?</h3>
    <br>
    <v-card>
      <v-card-text>
        <v-data-table
          v-model="selectedRows"
          :headers="headers"
          :items="tableItems"
          item-value="id"
          select-strategy="single"
          show-select
          density="compact"
          :disabled="isLocked"
          hide-default-footer
          :items-per-page="-1"
        >
          <!-- Custom header slots for subtitles -->
          <template v-slot:header.matchingData>
            <div class="text-center">
              <div>Matching Data</div>
              <div class="text-caption text-grey">Mismatches &amp; History</div>
            </div>
          </template>
          <template v-slot:header.masterData>
            <div class="text-center">
              <div>Master Data</div>
              <div class="text-caption text-grey">Lines, Products</div>
            </div>
          </template>

          <template v-slot:item.matchingData="{ item }">
            <template v-if="item.id === 1">
              <span class="text-grey">None</span>
            </template>
            <template v-else-if="item.id === 2">
              <v-autocomplete
                v-model="selectedParticipantRow2"
                :items="allParticipants"
                item-title="Participant_Name"
                item-value="Participant_SK"
                label="Select Participant"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                :disabled="isLocked"
                style="min-width: 200px;"
                @update:model-value="onParticipantChange(2, $event)"
              ></v-autocomplete>
            </template>
            <template v-else-if="item.id === 3">
              <v-autocomplete
                v-model="selectedParticipantRow3"
                :items="allParticipants"
                item-title="Participant_Name"
                item-value="Participant_SK"
                label="Select Participant"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                :disabled="isLocked"
                style="min-width: 200px;"
                @update:model-value="onParticipantChange(3, $event)"
              ></v-autocomplete>
            </template>
            <template v-else-if="item.id === 4">
              <v-autocomplete
                v-model="selectedBrands"
                :items="allBrands"
                item-title="Brand_Name"
                item-value="Brand_SK"
                label="Select Brand(s)"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                multiple
                chips
                closable-chips
                :disabled="isLocked"
                style="min-width: 300px;"
                @update:model-value="onBrandsChange"
              ></v-autocomplete>
            </template>
            <template v-else-if="item.id === 5">
              <v-chip variant="outlined" size="small">No Brand</v-chip>
            </template>
          </template>

          <template v-slot:item.masterData="{ item }">
            <template v-if="item.id === 1 || item.id === 2">
              <span>All</span>
            </template>
            <template v-else-if="item.id === 3">
              <v-text-field
                :model-value="selectedParticipantRow3Name"
                label="Participant (same)"
                density="compact"
                variant="outlined"
                hide-details
                readonly
                disabled
                style="min-width: 200px;"
              ></v-text-field>
            </template>
            <template v-else-if="item.id === 4">
              <v-text-field
                :model-value="selectedBrandsNames"
                label="Brand(s) (same)"
                density="compact"
                variant="outlined"
                hide-details
                readonly
                disabled
                style="min-width: 200px;"
              ></v-text-field>
            </template>
            <template v-else-if="item.id === 5">
              <v-autocomplete
                v-model="selectedParticipantRow5"
                :items="allParticipants"
                item-title="Participant_Name"
                item-value="Participant_SK"
                label="Select Participant"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                :disabled="isLocked"
                style="min-width: 200px;"
                @update:model-value="onParticipantChange(5, $event)"
              ></v-autocomplete>
            </template>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions class="justify-center pb-4">
        <v-btn
          color="primary"
          size="large"
          variant="elevated"
          :disabled="!isValidSelection || isLocked"
          @click="loadSelection"
        >
          Load Selection
        </v-btn>
        <v-btn
          v-if="isLocked"
          color="secondary"
          size="large"
          variant="outlined"
          prepend-icon="mdi-refresh"
          @click="refreshPage"
        >
          Refresh Page
        </v-btn>
      </v-card-actions>

      <!-- Loading Status Section -->
      <v-list v-if="isLocked" density="compact" class="mt-2 pb-4">
        <v-list-subheader>Loading Status</v-list-subheader>

        <!-- Product Matchings -->
        <v-list-item>
          <template v-slot:prepend>
            <template v-if="isProductMatchingsSkipped">
              <v-icon color="grey">mdi-minus-circle-outline</v-icon>
            </template>
            <template v-else-if="productMatchingsLoading">
              <v-progress-circular indeterminate size="20" width="2" color="primary" />
            </template>
            <template v-else>
              <v-icon color="success">mdi-check-circle</v-icon>
            </template>
          </template>
          <v-list-item-title>Product Matchings</v-list-item-title>
          <template v-slot:append>
            <span v-if="isProductMatchingsSkipped" class="text-grey">N/A</span>
            <span v-else-if="!productMatchingsLoading" class="text-grey">{{ productMatchingsCount.toLocaleString() }}</span>
          </template>
        </v-list-item>

        <!-- Lines -->
        <v-list-item>
          <template v-slot:prepend>
            <template v-if="linesLoading">
              <v-progress-circular indeterminate size="20" width="2" color="primary" />
            </template>
            <template v-else>
              <v-icon color="success">mdi-check-circle</v-icon>
            </template>
          </template>
          <v-list-item-title>Lines</v-list-item-title>
          <template v-slot:append>
            <span v-if="!linesLoading" class="text-grey">{{ linesCount.toLocaleString() }}</span>
          </template>
        </v-list-item>

        <!-- Products -->
        <v-list-item>
          <template v-slot:prepend>
            <template v-if="productsLoading">
              <v-progress-circular indeterminate size="20" width="2" color="primary" />
            </template>
            <template v-else>
              <v-icon color="success">mdi-check-circle</v-icon>
            </template>
          </template>
          <v-list-item-title>Products</v-list-item-title>
          <template v-slot:append>
            <span v-if="!productsLoading" class="text-grey">{{ productsCount.toLocaleString() }}</span>
          </template>
        </v-list-item>
      </v-list>
    </v-card>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { inject } from 'vue'

export default {
  name: 'DataSelectionTable',
  setup: () => ({ rc: inject('rc') }),
  data() {
    return {
      selectedRows: [1],
      selectedParticipantRow2: null,
      selectedParticipantRow3: null,
      selectedBrands: [],
      selectedParticipantRow5: null,
      headers: [
        {
          title: 'Matching Data',
          key: 'matchingData',
          sortable: false
        },
        {
          title: 'Master Data',
          key: 'masterData',
          sortable: false
        }
      ],
      tableItems: [
        { id: 1 },
        { id: 2 },
        { id: 3 },
        { id: 4 },
        { id: 5 }
      ]
    }
  },
  computed: {
    ...mapState(['selectionConfig']),
    allParticipants() {
      return this.rc?.allParticipants || []
    },
    allBrands() {
      return this.rc?.allBrands || []
    },
    isLocked() {
      return this.selectionConfig.isLocked
    },
    selectedRow() {
      return this.selectedRows.length > 0 ? this.selectedRows[0] : null
    },
    selectedParticipantRow3Name() {
      if (!this.selectedParticipantRow3) return ''
      const participant = this.allParticipants.find(p => p.Participant_SK === this.selectedParticipantRow3)
      return participant?.Participant_Name || ''
    },
    selectedBrandsNames() {
      if (!this.selectedBrands || this.selectedBrands.length === 0) return ''
      return this.selectedBrands.map(sk => {
        const brand = this.allBrands.find(b => b.Brand_SK === sk)
        return brand?.Brand_Name || ''
      }).filter(n => n).join(', ')
    },
    isValidSelection() {
      switch (this.selectedRow) {
        case 1:
          return true // None/All is always valid
        case 2:
          return !!this.selectedParticipantRow2
        case 3:
          return !!this.selectedParticipantRow3
        case 4:
          return this.selectedBrands && this.selectedBrands.length > 0
        case 5:
          return !!this.selectedParticipantRow5 // Participant required for No Brand
        default:
          return false
      }
    },
    // Loading states
    productMatchingsLoading() {
      return this.rc?.$apollo?.queries?.allProductMatchings?.loading || false
    },
    linesLoading() {
      return this.rc?.$apollo?.queries?.allLines?.loading || false
    },
    productsLoading() {
      return this.rc?.$apollo?.queries?.allProducts?.loading || false
    },
    // Counts
    productMatchingsCount() {
      return this.rc?.allProductMatchings?.length || 0
    },
    linesCount() {
      return this.rc?.allLines?.length || 0
    },
    productsCount() {
      return this.rc?.allProducts?.length || 0
    },
    // Check if query is skipped (row 1 skips allProductMatchings)
    isProductMatchingsSkipped() {
      return this.selectionConfig.rowId === 1
    }
  },
  watch: {
    selectedBrands(newVal) {
      this.setSelectionConfig({ brandSks: newVal || [] })
    },
    selectedParticipantRow3(newVal) {
      this.setSelectionConfig({ participantSk: newVal })
    }
  },
  methods: {
    ...mapMutations(['setSelectionConfig', 'lockSelection', 'setCurrentParticipantSk']),
    onParticipantChange(rowId, value) {
      // Auto-select the row when a value is chosen
      if (value) {
        this.selectedRows = [rowId]
      }
    },
    onBrandsChange(value) {
      // Auto-select row 4 when brands are chosen
      if (value && value.length > 0) {
        this.selectedRows = [4]
      }
    },
    refreshPage() {
      window.location.reload()
    },
    loadSelection() {
      if (!this.isValidSelection) return

      let config = {
        rowId: this.selectedRow,
        participantSk: null,
        brandSks: [],
        noBrand: false
      }

      switch (this.selectedRow) {
        case 2:
          config.participantSk = this.selectedParticipantRow2
          break
        case 3:
          config.participantSk = this.selectedParticipantRow3
          break
        case 4:
          config.brandSks = this.selectedBrands
          break
        case 5:
          config.participantSk = this.selectedParticipantRow5
          config.noBrand = true
          break
      }

      // Update selection config
      this.setSelectionConfig(config)

      // Also set currentParticipantSk for backward compatibility
      if (config.participantSk) {
        this.setCurrentParticipantSk(config.participantSk)
      }

      // Lock the selection
      this.lockSelection()

      // Emit event for parent components
      this.$emit('selection-loaded', config)
    },
    // External methods called from ProductMatchingEnrichmentOverview via ref
    setSelectionFromOverview(rowId, options = {}) {
      // Reset selections first (watchers will sync to store)
      this.selectedParticipantRow2 = null
      this.selectedParticipantRow3 = null
      this.selectedBrands = []
      this.selectedParticipantRow5 = null

      // Set up the selection based on rowId
      switch (rowId) {
        case 2:
          this.selectedParticipantRow2 = options.participantSk
          break
        case 3:
          this.selectedParticipantRow3 = options.participantSk
          break
        case 4:
          this.selectedBrands = options.brandSks || []
          break
        case 5:
          this.selectedParticipantRow5 = options.participantSk
          break
      }

      // Select the row
      this.selectedRows = [rowId]

      // If autoLoad option, immediately load the selection
      if (options.autoLoad) {
        this.$nextTick(() => {
          this.loadSelection()
        })
      }
    },
    addBrandToCurrentSelection(brandSk) {
      // Add a brand to current row 4 selection (watcher will sync to store)
      if (!this.selectedBrands.includes(brandSk)) {
        this.selectedBrands.push(brandSk)
      }
      this.selectedRows = [4]
    }
  }
}
</script>
